import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Button, Select, Spin, Tooltip, message } from "antd";
import { GetJavamonConfig, getJavamonImage, GetRandomJavamon } from "../../../../dao/jutge/GetJavamonConfig";
import { GetJutgeUsuariIdPoints } from "../../../../dao/jutge/GetJutgeUsuariIdPoints";
import { AuthContext } from "../../../../config/AuthContext";
import { DeleteJutgeUsuariIdPointsId } from "../../../../dao/jutge/DeleteJutgeUsuariIdPointsId";
import { PostJavadexSeen } from "../../../../dao/jutge/PostJavadexSeen";
import { TbPokeball } from "react-icons/tb";

const { Option } = Select;

type BolaItem = {
    id: number;
    tipo: string;
    color: string;
    punts: number;
    descripcio?: string;
    ball?: string | null;
};

type ForcedJavamon = {
    id?: number;
    name: string;
    image: string;
    rarity: string;
};

interface JavaMonProps {
    onClose: () => void;
    userId: string;
    forcedJavamon?: ForcedJavamon | null;
    onSuccess?: () => void;
    idSeason?: number;
}

const JavaMon: React.FC<JavaMonProps> = ({ onClose, userId, forcedJavamon, onSuccess, idSeason = 1 }) => {
    const [searching, setSearching] = useState(true);
    const { user } = useContext(AuthContext);
    const [balls, setBalls] = useState<BolaItem[]>([]);
    const [selectedBallId, setSelectedBallId] = useState<number | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [text, setText] = useState("¡Un JAVAmon salvaje ha aparecido!");
    const [dotProgress, setDotProgress] = useState("");
    const [captureResult, setCaptureResult] = useState<"idle" | "success" | "fail">("idle");
    const [ballFlyPose, setBallFlyPose] = useState<{ visible: boolean; left: number; top: number }>({
        visible: false, left: 0, top: 0
    });
    const containerRef = useRef<HTMLDivElement | null>(null);
    const ballElRef = useRef<HTMLDivElement | null>(null);

    const [javamonId, setJavamonId] = useState<number | null>(null);
    const [javamonImg, setJavamonImg] = useState<string | null>(null);
    const [javamonName, setJavamonName] = useState<string | null>(null);
    const [javamonRarity, setJavamonRarity] = useState<string | null>(null);
    const [rarityColor, setRarityColor] = useState<string>("#fff");

    const pickRandom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

    type CapturedJavamon = {
        name: string;
        targetJavamonName?: string;
        javamonId?: number | null;
        ballName?: string;
        ballPoints?: number;
        captured?: boolean;
        origen?: string;
        roll?: number;
        maxRoll?: number;
        rarity: string;
        image: string;
        tier: string;
        capturedAt: string;
    };

    const initDone = useRef(false);
    useEffect(() => {
        const initGame = async () => {
            if (initDone.current) return;
            initDone.current = true;
            if (forcedJavamon) {
                setJavamonId((forcedJavamon as any).id ?? null);
                setJavamonRarity(forcedJavamon.rarity);
                setJavamonName(forcedJavamon.name);
                setJavamonImg(forcedJavamon.image);
                setText(`¡Un ${forcedJavamon.name} salvaje ha aparecido!`);
                setSearching(false);
            } else {
                try {
                    const data = await GetRandomJavamon(idSeason ? idSeason.toString() : "", userId ? userId.toString() : "");
                    if (!data || !data.javamon || !data.rarity) {
                        message.error("Error del servidor: No se pudo obtener el Javamon aleatorio.");
                        onClose();
                        return;
                    }

                    const picked = data.javamon;
                    const selectedRarity = data.rarity;

                    setJavamonId(picked.id ?? null);
                    setJavamonRarity(selectedRarity.nom);
                    setRarityColor(selectedRarity.color);
                    setJavamonName(picked.nom);
                    setJavamonImg(getJavamonImage(picked));

                    setText(`¡Un ${picked.nom} salvaje ha aparecido!`);
                } catch (e: any) {
                    console.error("Error en JavaMon initGame:", e);
                    message.error("Excepción en minijuego Javamon. Mira la consola.");
                    onClose();
                }
            }
        };
        initGame();
    }, [forcedJavamon, idSeason, onClose]);

    useEffect(() => {
        if (javamonImg && userId) {
            const parts = javamonImg.split('/');
            const filename = parts[parts.length - 1];
            PostJavadexSeen(userId, filename);
        }
    }, [javamonImg, userId]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setSearching(true);
                const res: any = await GetJutgeUsuariIdPoints(userId + "");
                if (!mounted) return;
                const parsed: BolaItem[] = Array.isArray(res)
                    ? res
                        .filter((b: any) => (b.ball === 'JAVAball' || (b.ball === null && b.color !== null)))
                        .map((b: any) => ({
                            id: b.id,
                            tipo: b.tipo,
                            color: b.color,
                            punts: b.punts,
                            descripcio: b.descripcio,
                            ball: b.ball ?? null,
                        }))
                    : [];

                if (forcedJavamon && (user.tipus === "1" || user.tipus === "0")) {
                    parsed.push({ id: 0, tipo: "especial", color: "#000000", punts: 150 });
                    if (!selectedBallId) setSelectedBallId(0);
                }

                if (parsed.length === 0) {
                    parsed.push({ id: -2, tipo: "Básica (1%)", color: "#8c8c8c", punts: 2, descripcio: "Bola de emergencia" });
                }

                setBalls(parsed);
                if (!selectedBallId && parsed.length > 0) setSelectedBallId(parsed[0].id);
            } catch (err) {
                message.error("Error cargando bolas.");
            } finally {
                if (mounted) setSearching(false);
            }
        })();
        return () => { mounted = false; };
    }, [userId, user.tipus, forcedJavamon]);

    function getBallById(id: number | null) {
        if (id == null) return null;
        return balls.find(b => b.id === id) ?? null;
    }

    function startDotAnimation() {
        setDotProgress("");
        const steps = [" .", " ..", " ...", " ...."];
        let idx = 0;
        const iv = setInterval(() => {
            setDotProgress(steps[idx % steps.length]);
            idx++;
        }, 400);
        return () => clearInterval(iv);
    }

    async function handleIntentarCapturar() {
        if (isCapturing) return;
        const selected = getBallById(selectedBallId);
        if (!selected) {
            message.error("Selecciona una bola.");
            return;
        }

        setBalls(prev => {
            const next = prev.filter(b => b.id !== selected.id);
            if (next.length > 0) {
                setSelectedBallId(next[0].id);
            } else {
                setSelectedBallId(null);
            }
            return next;
        });
        setIsCapturing(true);
        setCaptureResult("idle");
        const stopDots = startDotAnimation();

        const container = containerRef.current;
        if (container) {
            const containerRect = container.getBoundingClientRect();
            const originRect = ballElRef.current?.getBoundingClientRect();
            const origin = originRect ? {
                left: originRect.left - containerRect.left + originRect.width / 2,
                top: originRect.top - containerRect.top + originRect.height / 2
            } : { left: 80, top: containerRect.height - 80 };

            const dest = { left: containerRect.width - 170, top: containerRect.height - 180 };
            setBallFlyPose({ visible: true, left: origin.left - 20, top: origin.top - 20 });

            await new Promise<void>(res => {
                requestAnimationFrame(() => {
                    setBallFlyPose({ visible: true, left: dest.left, top: dest.top });
                    setTimeout(() => res(), 900);
                });
            });
        }

        await new Promise(res => setTimeout(res, 600));
        stopDots();

        const chance = Math.max(0, Math.min(1, (selected.punts ?? 0) / 200));
        const rollRatio = Math.random();
        const rollValue = Number((rollRatio * 200).toFixed(2));
        const success = rollRatio < chance;

        const capturedJavamon: CapturedJavamon = {
            name: success ? (javamonName ?? "unknown") : "",
            targetJavamonName: javamonName ?? "unknown",
            javamonId: javamonId,
            ballName: selected.tipo,
            ballPoints: selected.punts,
            captured: success,
            origen: "WEB",
            roll: rollValue,
            maxRoll: 200,
            rarity: javamonRarity ?? "normal",
            image: javamonImg ?? "",
            tier: "dynamic",
            capturedAt: new Date().toISOString()
        };

        try {
            await DeleteJutgeUsuariIdPointsId(userId + "", selected.id + "", capturedJavamon);
        } catch (err) {
            console.error("Error al consumir bola / registrar Javamon:", err);
        }

        if (success) {
            setCaptureResult("success");
            setText("¡Has capturado al JAVAmon!");
            setIsCapturing(false);
            if (onSuccess) onSuccess();
        } else {
            setCaptureResult("fail");
            setText("El JAVAmon escapó...");
            await new Promise(res => setTimeout(res, 1400));
            onClose();
        }
        setDotProgress("");
    }

    const mochila = useMemo(() => {
        const map: Record<string, { count: number; color: string }> = {};
        balls.forEach(b => {
            if (!map[b.tipo]) map[b.tipo] = { count: 0, color: b.color };
            map[b.tipo].count++;
        });
        return map;
    }, [balls]);

    const rarityGlow = useMemo(() => `drop-shadow(0 0 22px ${rarityColor}ee)`, [rarityColor]);
    const rarityLabel = useMemo(() => {
        if (!javamonRarity || javamonRarity.toLowerCase() === 'normal') return null;
        return { text: javamonRarity.toUpperCase(), color: rarityColor };
    }, [javamonRarity, rarityColor]);

    const Confetti = () => {
        if (captureResult !== "success") return null;
        const pieces = new Array(18).fill(0);
        return (
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
                {pieces.map((_, i) => (
                    <motion.div
                        key={i}
                        initial={{ y: -20, opacity: 1 }}
                        animate={{ y: 200 + Math.random() * 100, rotate: Math.random() * 360, opacity: 0 }}
                        transition={{ duration: 1.2 + Math.random() * 0.6, delay: Math.random() * 0.4 }}
                        style={{
                            position: "absolute",
                            left: `${20 + Math.random() * 60}%`,
                            top: 20,
                            width: 10,
                            height: 14,
                            background: ["#ff4d4f", "#ffd666", "#95de64", "#69c0ff", "#b37feb"][i % 5],
                            transformOrigin: "center"
                        }}
                    />
                ))}
            </div>
        );
    };

    return (
        <div ref={containerRef} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 99999 }}>
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }} style={{ position: "relative", width: 720, height: 420, borderRadius: 16, overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.6)", background: "#111" }}>
                <motion.img src="/images/javamon/t1/javamonworld.jpg" alt="world" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.0 }} />

                {javamonImg && (
                    <motion.img src={javamonImg} alt={javamonName ?? "javamon"} style={{ position: "absolute", bottom: 120, right: 140, width: 160, imageRendering: "pixelated", filter: rarityGlow, zIndex: 6 }} animate={captureResult === "fail" ? { x: 300, y: -60, rotate: 10, opacity: 0 } : { x: 0, y: 0 }} transition={{ duration: captureResult === "fail" ? 0.9 : 0.4 }} />
                )}

                {rarityLabel && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} style={{ position: "absolute", bottom: 290, right: 460, fontFamily: "monospace", fontSize: 24, fontWeight: "bold", color: rarityLabel.color, textShadow: `0 0 8px ${rarityLabel.color}`, zIndex: 7 }}>{rarityLabel.text}</motion.div>
                )}

                <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.35 }} style={{ position: "absolute", bottom: 0, width: "100%", padding: 16, background: "linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.75))", color: "white", fontFamily: "monospace", zIndex: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center" }}>{searching ? <Spin /> : null}</div>
                            <div style={{ fontSize: 18 }}>
                                <div style={{ minHeight: 22 }}>{text}{dotProgress}</div>
                                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>{searching ? "Buscando objetos..." : `${balls.length} bola(s) disponibles`}</div>
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            {Object.entries(mochila).map(([tipo, info]) => (
                                <Tooltip key={tipo} title={`${tipo} — x${info.count}`}>
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <TbPokeball style={{ width: 44, height: 44, color: info.color || "#ccc" }} />
                                        <div style={{ fontSize: 12, marginTop: 6 }}>{tipo} ({info.count})</div>
                                    </div>
                                </Tooltip>
                            ))}
                        </div>
                    </div>

                    <div style={{ marginTop: 14, display: "flex", gap: 12, justifyContent: "flex-end", alignItems: "center" }}>
                        <div ref={ballElRef}>
                            <Select dropdownStyle={{ zIndex: 100000 }} value={selectedBallId ?? undefined} style={{ width: 260 }} onChange={(val) => setSelectedBallId(Number(val))} disabled={isCapturing || searching || balls.length === 0 || captureResult === "success"} placeholder="Elige una bola...">
                                {balls.map(b => (
                                    <Option key={b.id} value={b.id}>
                                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                            <TbPokeball style={{ width: 16, height: 16, color: b.color }} />
                                            <span>{b.tipo} ({b.punts} pts)</span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <Button danger disabled={isCapturing || captureResult === "success"} onClick={onClose}>Dejar escapar</Button>
                            {captureResult !== "success" ? (
                                <Button type="primary" disabled={isCapturing || searching || selectedBallId == null} onClick={handleIntentarCapturar}>{isCapturing ? "Lanzando..." : "Intentar capturar"}</Button>
                            ) : (
                                <Button type="primary" onClick={onClose}>Salir</Button>
                            )}
                        </div>
                    </div>
                </motion.div>

                {ballFlyPose.visible && (
                    <motion.div initial={{ left: ballFlyPose.left, top: ballFlyPose.top, scale: 0.6, opacity: 0 }} animate={{ left: ballFlyPose.left, top: ballFlyPose.top, scale: 1, opacity: 1 }} transition={{ duration: 0.9, ease: "easeOut" }} style={{ position: "absolute", width: 44, height: 44, zIndex: 20, pointerEvents: "none" }}>
                        <div style={{ width: 44, height: 44, borderRadius: 22, background: "#fff", border: "3px solid rgba(0,0,0,0.3)", boxShadow: "0 6px 18px rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <div style={{ width: 36, height: 18, background: getBallById(selectedBallId)?.color ?? "#ccc", position: "absolute", top: 6, left: "50%", transform: "translateX(-50%)", borderTopLeftRadius: 18, borderTopRightRadius: 18 }} />
                            <div style={{ position: "absolute", width: 16, height: 16, borderRadius: 8, background: "#eee", border: "3px solid rgba(0,0,0,0.35)" }} />
                        </div>
                    </motion.div>
                )}
                <Confetti />
            </motion.div>
        </div>
    );
};

export default JavaMon;
